
import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import { ArrowUp, ChevronRight, Shield, Lock, User, Database, FileText, Globe, Mail, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

// Icons for each section
const sectionIcons = {
  introduction: <Shield className="h-6 w-6 text-blue-500" />,
  commitment: <Lock className="h-6 w-6 text-purple-500" />,
  rights: <User className="h-6 w-6 text-emerald-500" />,
  legal: <FileText className="h-6 w-6 text-amber-500" />,
  protection: <Database className="h-6 w-6 text-red-500" />,
  international: <Globe className="h-6 w-6 text-sky-500" />,
  dpo: <User className="h-6 w-6 text-indigo-500" />,
  exercising: <ChevronRight className="h-6 w-6 text-green-500" />,
  contact: <Mail className="h-6 w-6 text-orange-500" />
};

// Reusable section component for better organization
const ComplianceSection = ({ id, title, icon, children, className }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.5 }}
    >
      <Card 
        id={id}
        className={cn(
          "mb-8 shadow-lg border-0 bg-white/70 backdrop-blur-sm hover:shadow-xl transition-all duration-300",
          className
        )}
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <div className="p-2 rounded-full bg-gradient-to-br from-blue-50 to-purple-50 shadow-sm">
              {icon}
            </div>
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4 pt-4">
          {children}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Quick navigation component
const TableOfContents = () => {
  const sections = [
    { id: "introduction", title: "Introduction" },
    { id: "commitment", title: "Our Commitment" },
    { id: "rights", title: "Your Rights" },
    { id: "legal", title: "Legal Basis" },
    { id: "protection", title: "Data Protection" },
    { id: "international", title: "International Transfers" },
    { id: "dpo", title: "Data Protection Officer" },
    { id: "exercising", title: "Exercising Rights" },
    { id: "contact", title: "Contact" }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="sticky top-20 hidden lg:block"
    >
      <Card className="shadow-md bg-white/80 backdrop-blur-sm border-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium text-gray-800">Quick Navigation</CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <ul className="space-y-1">
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="text-sm text-gray-600 hover:text-blue-600 flex items-center gap-1.5 py-1 px-2 rounded hover:bg-blue-50 transition-colors group"
                >
                  <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const GDPRCompliance = () => {
  // Back to top button visibility control
  const [showBackToTop, setShowBackToTop] = React.useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>GDPR Compliance | DGK Freelance Hub</title>
        <meta name="description" content="Learn about our GDPR compliance policies and how we protect your personal data at DGK Freelance Hub." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-blue-50/50 to-purple-50/50">
        <div className="absolute inset-0 bg-[url('/patterns/grid.svg')] bg-fixed opacity-5 pointer-events-none"></div>
        
        <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8 max-w-7xl relative">
          {/* Breadcrumbs */}
          <div className="text-sm text-gray-500 mb-6">
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li className="inline-flex items-center">
                  <a href="/" className="hover:text-blue-600 transition-colors">Home</a>
                </li>
                <li>
                  <div className="flex items-center">
                    <span className="mx-2">/</span>
                    <a href="/legal" className="hover:text-blue-600 transition-colors">Legal</a>
                  </div>
                </li>
                <li aria-current="page">
                  <div className="flex items-center">
                    <span className="mx-2">/</span>
                    <span className="text-gray-700 font-medium">GDPR Compliance</span>
                  </div>
                </li>
              </ol>
            </nav>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Left sidebar on large screens */}
            <div className="lg:col-span-1">
              <TableOfContents />
            </div>

            {/* Main content */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-12 text-center"
              >
                <div className="inline-block p-1.5 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 mb-4">
                  <div className="bg-white p-2 rounded-full">
                    <Shield className="h-10 w-10 text-blue-600" />
                  </div>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-400 bg-clip-text text-transparent mb-4">
                  GDPR Compliance
                </h1>
                <p className="text-gray-500 text-lg max-w-3xl mx-auto">
                  Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <Separator className="my-8" />
              </motion.div>

              <ComplianceSection 
                id="introduction" 
                title="Introduction to GDPR" 
                icon={sectionIcons.introduction}
                className=""
              >
                <p>
                  The General Data Protection Regulation (GDPR) is a comprehensive data protection law that came into effect on May 25, 2018, in the European Union (EU) and European Economic Area (EEA). At DGK Freelance Hub, we are committed to ensuring full compliance with GDPR requirements and protecting the privacy rights of all our users.
                </p>
                <p>
                  This page outlines our approach to GDPR compliance and explains how we uphold your data protection rights when you use our platform.
                </p>
              </ComplianceSection>

              <ComplianceSection 
                id="commitment" 
                title="Our Commitment to GDPR Compliance" 
                icon={sectionIcons.commitment}
                className=""
              >
                <p>
                  DGK Freelance Hub is dedicated to protecting your personal data and respecting your privacy rights. We have implemented a comprehensive GDPR compliance program that includes:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Updating our Privacy Policy and Terms of Service to ensure transparency about data processing</li>
                  <li>Implementing technical and organizational measures to protect personal data</li>
                  <li>Establishing clear procedures for handling data subject rights requests</li>
                  <li>Conducting data protection impact assessments for high-risk processing activities</li>
                  <li>Maintaining records of processing activities</li>
                  <li>Ensuring appropriate safeguards for international data transfers</li>
                  <li>Training our staff on data protection and privacy matters</li>
                </ul>
              </ComplianceSection>

              <ComplianceSection 
                id="rights" 
                title="Your Data Rights Under GDPR" 
                icon={sectionIcons.rights}
                className=""
              >
                <p>
                  Under the GDPR, individuals have enhanced rights regarding their personal data. As a DGK Freelance Hub user, you have the right to:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100 hover:shadow-md transition-all">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <div className="p-1 rounded-full bg-blue-100">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                      Right to Access
                    </h3>
                    <p className="text-gray-600 text-sm">
                      You have the right to request a copy of the personal data we hold about you and to check that we are lawfully processing it.
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100 hover:shadow-md transition-all">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <div className="p-1 rounded-full bg-blue-100">
                        <FileText className="h-4 w-4 text-blue-600" />
                      </div>
                      Right to Rectification
                    </h3>
                    <p className="text-gray-600 text-sm">
                      You have the right to request correction of any inaccurate personal data we hold about you, or to complete any incomplete personal data.
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100 hover:shadow-md transition-all">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <div className="p-1 rounded-full bg-blue-100">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      </div>
                      Right to Erasure
                    </h3>
                    <p className="text-gray-600 text-sm">
                      In certain circumstances, you have the right to request the deletion of your personal data when there is no compelling reason for its continued processing.
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100 hover:shadow-md transition-all">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <div className="p-1 rounded-full bg-blue-100">
                        <Lock className="h-4 w-4 text-purple-600" />
                      </div>
                      Right to Restrict Processing
                    </h3>
                    <p className="text-gray-600 text-sm">
                      You have the right to request that we restrict the processing of your personal data in certain circumstances, such as when you contest the accuracy of the data.
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100 hover:shadow-md transition-all">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <div className="p-1 rounded-full bg-blue-100">
                        <Database className="h-4 w-4 text-emerald-600" />
                      </div>
                      Right to Data Portability
                    </h3>
                    <p className="text-gray-600 text-sm">
                      You have the right to request the transfer of your personal data to yourself or to a third party in a structured, commonly used, machine-readable format.
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100 hover:shadow-md transition-all">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <div className="p-1 rounded-full bg-blue-100">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-amber-600">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                        </svg>
                      </div>
                      Right to Object
                    </h3>
                    <p className="text-gray-600 text-sm">
                      You have the right to object to the processing of your personal data in certain circumstances, including for direct marketing purposes or when processing is based on our legitimate interests.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100 mt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <div className="p-1 rounded-full bg-blue-100">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-indigo-600">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                      </svg>
                    </div>
                    Rights Related to Automated Decision Making and Profiling
                  </h3>
                  <p className="text-gray-600 text-sm">
                    You have rights related to automated decision-making and profiling, including the right not to be subject to decisions based solely on automated processing that produce legal or similarly significant effects.
                  </p>
                </div>
              </ComplianceSection>

              <ComplianceSection 
                id="legal" 
                title="Legal Basis for Processing" 
                icon={sectionIcons.legal}
                className=""
              >
                <p>
                  Under the GDPR, we must have a valid legal basis for processing your personal data. The legal bases we rely on include:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-100 hover:shadow-md transition-all">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Consent</h3>
                    <p className="text-gray-600 text-sm">
                      Where you have given clear consent for us to process your personal data for a specific purpose.
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-100 hover:shadow-md transition-all">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Contract</h3>
                    <p className="text-gray-600 text-sm">
                      Where processing is necessary for the performance of a contract with you or to take steps at your request before entering into a contract.
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-100 hover:shadow-md transition-all">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Legal Obligation</h3>
                    <p className="text-gray-600 text-sm">
                      Where processing is necessary for compliance with a legal obligation to which we are subject.
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-100 hover:shadow-md transition-all">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Legitimate Interests</h3>
                    <p className="text-gray-600 text-sm">
                      Where processing is necessary for our legitimate interests or the legitimate interests of a third party, except where such interests are overridden by your interests or fundamental rights and freedoms.
                    </p>
                  </div>
                </div>
              </ComplianceSection>

              <ComplianceSection 
                id="protection" 
                title="Data Protection Measures" 
                icon={sectionIcons.protection}
                className=""
              >
                <p>
                  We implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including:
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                  {[
                    { icon: <Lock className="h-5 w-5 text-blue-500" />, text: "Encryption of personal data" },
                    { icon: <AlertCircle className="h-5 w-5 text-amber-500" />, text: "Regular security testing" },
                    { icon: <User className="h-5 w-5 text-green-500" />, text: "Access controls" },
                    { icon: <Database className="h-5 w-5 text-purple-500" />, text: "Data minimization" },
                    { icon: <FileText className="h-5 w-5 text-indigo-500" />, text: "Staff training" },
                    { icon: <Shield className="h-5 w-5 text-red-500" />, text: "Incident response procedures" },
                  ].map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border border-gray-100 hover:shadow-md transition-all"
                    >
                      <div className="p-1.5 bg-white rounded-full shadow-sm">
                        {item.icon}
                      </div>
                      <span className="text-gray-700 text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>
                
                <div className="p-3 mt-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-blue-800 text-sm flex items-start gap-2">
                    <Shield className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong className="font-medium">Security commitment:</strong> We regularly review and update our security measures to protect against evolving threats and ensure the highest level of data protection.
                    </span>
                  </p>
                </div>
              </ComplianceSection>

              <ComplianceSection 
                id="international" 
                title="International Data Transfers" 
                icon={sectionIcons.international}
                className=""
              >
                <p>
                  When we transfer personal data outside the EU/EEA, we ensure appropriate safeguards are in place through one or more of the following mechanisms:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                  <li>Binding Corporate Rules (BCRs)</li>
                  <li>Adequacy decisions by the European Commission for countries providing adequate protection</li>
                  <li>Explicit consent from data subjects in specific circumstances</li>
                </ul>
                
                <div className="p-3 mt-4 bg-gradient-to-r from-sky-50 to-indigo-50 rounded-lg border border-sky-100">
                  <div className="flex gap-3">
                    <Globe className="h-6 w-6 text-sky-500 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-800 mb-1">Privacy Shield Update</h4>
                      <p className="text-gray-600 text-sm">
                        Following the invalidation of the EU-US Privacy Shield by the Court of Justice of the European Union (Schrems II decision), we have implemented additional safeguards for data transfers to the United States and other non-EEA countries.
                      </p>
                    </div>
                  </div>
                </div>
              </ComplianceSection>

              <ComplianceSection 
                id="dpo" 
                title="Data Protection Officer" 
                icon={sectionIcons.dpo}
                className=""
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6 bg-gradient-to-r from-indigo-50 to-purple-50 p-5 rounded-lg border border-indigo-100">
                  <div className="flex-shrink-0 mx-auto md:mx-0">
                    <div className="w-24 h-24 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full flex items-center justify-center shadow-lg">
                      <User className="h-12 w-12 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Our Data Protection Officer</h3>
                    <p className="text-gray-600">
                      We have appointed a Data Protection Officer (DPO) responsible for overseeing our data protection strategy and implementation to ensure compliance with GDPR requirements.
                    </p>
                    <p className="text-gray-600 mt-2">
                      Our DPO serves as a point of contact for data subjects and supervisory authorities on matters relating to the processing of personal data.
                    </p>
                  </div>
                </div>
              </ComplianceSection>

              <ComplianceSection 
                id="exercising" 
                title="Exercising Your Rights" 
                icon={sectionIcons.exercising}
                className=""
              >
                <p>
                  To exercise any of your data rights under the GDPR, please contact our Data Protection Officer using the contact information below. We will respond to your request within one month of receipt.
                </p>
                <p>
                  Please note that we may need to verify your identity before processing your request to ensure the security of your personal data.
                </p>
                
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-100 mt-4">
                  <div className="p-2 bg-white rounded-full shadow-sm">
                    <ChevronRight className="h-6 w-6 text-green-500" />
                  </div>
                  <p className="text-green-800 text-sm">
                    <strong className="font-medium">Response time:</strong> While we strive to respond to all requests within 30 days, complex requests may require an extension of up to two additional months, in accordance with GDPR provisions.
                  </p>
                </div>
              </ComplianceSection>

              <ComplianceSection 
                id="contact" 
                title="Contact Information" 
                icon={sectionIcons.contact}
                className="bg-gradient-to-br from-white to-blue-50"
              >
                <p>
                  If you have any questions about our GDPR compliance or would like to exercise your data rights, please contact our Data Protection Officer at:
                </p>
                <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100 mt-4 shadow-sm hover:shadow-md transition-all">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex gap-3 items-center">
                      <div className="p-2 bg-blue-100 rounded-full">
                        <Mail className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase font-medium">Email</div>
                        <div className="text-gray-800">dpo@dgkfreelancehub.com</div>
                      </div>
                    </div>
                    
                    <div className="flex gap-3 items-center">
                      <div className="p-2 bg-purple-100 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-purple-600">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase font-medium">Phone</div>
                        <div className="text-gray-800">+1 (555) 123-4567</div>
                      </div>
                    </div>
                    
                    <div className="flex gap-3 items-center md:col-span-2">
                      <div className="p-2 bg-indigo-100 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-indigo-600">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase font-medium">Address</div>
                        <div className="text-gray-800">123 Blockchain Boulevard, Crypto City, CC 12345</div>
                      </div>
                    </div>
                  </div>
                  
                  <Button className="mt-4 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-lg shadow-blue-200/50 w-full sm:w-auto">
                    Contact Our DPO
                  </Button>
                </div>
              </ComplianceSection>

              <div className="text-center mt-16 mb-8 space-y-4">
                <div className="inline-block p-1.5 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200">
                  <Shield className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Your Privacy is Our Priority</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  DGK Freelance Hub is committed to maintaining the highest standards of data protection and GDPR compliance. We continuously review and improve our privacy practices to ensure your personal data remains secure.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
            <p>© {new Date().getFullYear()} DGK Freelance Hub. All rights reserved.</p>
            <div className="flex justify-center gap-4 mt-2 text-gray-600">
              <a href="/privacy-policy" className="hover:text-blue-600 transition-colors">Privacy Policy</a>
              <span>•</span>
              <a href="/terms-of-service" className="hover:text-blue-600 transition-colors">Terms of Service</a>
              <span>•</span>
              <a href="/cookie-policy" className="hover:text-blue-600 transition-colors">Cookie Policy</a>
            </div>
          </div>
        </div>
        
        {/* Back to top button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: showBackToTop ? 1 : 0 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg z-50 transition-all duration-300 hover:scale-110"
          aria-label="Back to top"
        >
          <ArrowUp className="h-5 w-5" />
        </motion.button>
      </div>
    </>
  );
};

export default GDPRCompliance;
